import { themeGet } from '@styled-system/theme-get'
import { Badge, Box, Card, Flex, Image, Link, Relative, Text, Truncate } from 'pcln-design-system'
import { User } from 'pcln-icons'
import React from 'react'
import styled from 'styled-components'
import FavoriteToggle from './Favorite/FavoriteToggle'

const NoWrapText = styled(Text)`
  ${themeGet('mediaQueries.lg')} {
    white-space: nowrap;
  }
`
const NoWrapDateText = styled(Text)`
  ${themeGet('mediaQueries.md')} {
    white-space: nowrap;
  }
`

const StyledFlex = styled(Flex)`
  gap: ${themeGet('space.2')};
`

const StyledWrapper = styled(Box)`
  display: grid;
  row-gap: ${themeGet('space.1')};
`

const StyledImage = styled(Image)`
  object-fit: cover;
`

const StyledCard = styled(Card)`
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`

const StyledTruncate = styled(Truncate)`
  white-space: normal;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
`

const LinkOverlay = styled(Link)`
  position: absolute;
  inset: 0 0 0 0;
  &:hover + ${Card} {
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`

const StyledLink = styled(Link)`
  z-index: 0;
`

const DealTruncate = ({ isRetail, isSmallerView, description }) => {
  return isRetail ? (
    <Truncate textStyle={['heading5', null, null, null, isSmallerView ? null : 'heading4']}>
      {description}
    </Truncate>
  ) : (
    <StyledTruncate textStyle={['heading5', null, null, null, isSmallerView ? null : 'heading4']}>
      {description}
    </StyledTruncate>
  )
}

const DateBox = ({ title, date, isSmallerView }) => {
  return (
    <Flex
      flexDirection='column'
      bg='background.light'
      justifyContent='center'
      textAlign='center'
      borderRadius='md'
      p={[3, null, 2, null, 3]}
      width={[1, null, 'auto']}
      minWidth={isSmallerView ? [null, null, null, null, 135] : null}
    >
      <NoWrapDateText fontSize={0} color='text.light' bold>
        {title}
      </NoWrapDateText>
      <NoWrapDateText
        textStyle={['heading6', null, 'captionBold', null, isSmallerView ? 'captionBold' : 'heading6']}
      >
        {date}
      </NoWrapDateText>
    </Flex>
  )
}

const RoomAdultBox = ({ numAdults, numRooms, flexDirection, px }) => {
  return (
    <Flex
      flexDirection={flexDirection}
      justifyContent='center'
      alignItems='center'
      bg='background.light'
      borderRadius='md'
      py={[2, null, null, null, 1]}
      px={px}
      minWidth={[null, null, null, null, 140]}
      style={{ columnGap: '4px' }}
    >
      <User size={20} color='text.light' />
      <NoWrapText textStyle='caption' textAlign='center'>{`${numAdults} Adult${
        numAdults > 1 ? 's' : ''
      }, ${numRooms} Room${numRooms > 1 ? 's' : ''}`}</NoWrapText>
    </Flex>
  )
}

const BookNowButton = ({ linkUrl, ariaLabel, onClick, buttonText, color }) => (
  <StyledLink
    href={linkUrl}
    variation='fill'
    target='_blank'
    size={['medium', null, 'small', null, 'large', null]}
    my={[null, null, null, null, null, 'auto']}
    aria-label={ariaLabel}
    onClick={onClick}
    color={color}
    style={{ minWidth: 'max-content', borderRadius: '24px' }}
  >
    {buttonText}
  </StyledLink>
)

const TripActivity = ({
  headline,
  subheadline,
  checkInDate,
  checkOutDate,
  numAdults,
  numRooms,
  linkUrl,
  ariaLabel,
  onClick,
  buttonText,
  isSmallerView,
  hotelDealType,
  numReviews,
  numStars,
  overallRating,
  imageSrc,
  altText,
  hotelPriceDetails,
  initialFavoriteState,
  onFavoriteToggle,
  withSibling,
}) => {
  const isExpressDeal =
    hotelDealType === 'EXPRESS' || hotelDealType === 'EXPRESS_DEAL' || hotelDealType === 'SopqHotel'
  const isRetail = hotelDealType === 'RETAIL' || hotelDealType === 'RtlHotel'
  const isPriceBreakers = hotelDealType === 'PRICEBREAKER' || hotelDealType === 'AbandonedPriceBreakersHotel'

  const expressImageSrc = isExpressDeal
    ? 'https://s1.pclncdn.com/design-assets/home/recent-activity/express-deal-bed.png'
    : 'https://s1.pclncdn.com/design-assets/home/recent-activity/pricebreakers-hotel.png'
  const expressLogoImageSrc = `https://s1.pclncdn.com/design-assets/home/recent-activity/${
    isExpressDeal ? 'express-deals-logo-refresh' : 'pricebreakers-logo'
  }.png`

  return (
    <Relative>
      <LinkOverlay href={linkUrl} target='_blank' role='link' aria-label={ariaLabel} onClick={onClick} />
      <StyledCard bg='background.lightest' borderRadius='xl' boxShadowSize='md' p={3} borderWidth={0}>
        <StyledFlex flexDirection={['column', null, 'row']} justifyContent='space-between'>
          <StyledFlex>
            <Relative style={{ flexShrink: 0 }}>
              <StyledImage
                width={isSmallerView ? 98 : [98, null, null, null, 105]}
                height={[78, null, '100%']}
                borderRadius='xl'
                src={isRetail ? imageSrc : `${expressImageSrc}?opto&auto=avif,webp`}
                alt={altText}
              />
              {onFavoriteToggle && (
                <FavoriteToggle
                  initialFavoriteState={initialFavoriteState}
                  onFavoriteToggle={onFavoriteToggle}
                />
              )}
            </Relative>
            <StyledWrapper my='auto'>
              {(isExpressDeal || isPriceBreakers) && (
                <Image width={112} height={16} src={`${expressLogoImageSrc}?opto&auto=avif,webp`} />
              )}
              <DealTruncate isRetail={isRetail} isSmallerView={isSmallerView} description={headline} />
              <Text textStyle='caption' color='text.light' lineHeight={2}>
                {subheadline}
                {isPriceBreakers && (
                  <>
                    <br />
                    The hotel will be revealed after you book
                  </>
                )}
              </Text>
              {isRetail && (
                <StyledFlex>
                  {numReviews > 0 && !isNaN(overallRating) && (
                    <Badge color='alert' size='small' my='auto'>
                      {overallRating}
                    </Badge>
                  )}
                  <Text fontSize={0} my='auto'>
                    {numReviews > 0 ? `${numReviews} Reviews | ${numStars}-STAR` : `${numStars}-STAR`}
                  </Text>
                </StyledFlex>
              )}
            </StyledWrapper>
          </StyledFlex>
          {hotelPriceDetails ? (
            <StyledFlex flexDirection={['column', null, 'row']}>
              <StyledFlex
                flexDirection={['column', null, null, null, 'row']}
                justifyContent='center'
                mr={[0, 0, 0, 2, 3, 4]}
              >
                <StyledFlex>
                  <DateBox title='CHECK-IN' date={checkInDate} isSmallerView={isSmallerView} />
                  <DateBox title='CHECK-OUT' date={checkOutDate} isSmallerView={isSmallerView} />
                </StyledFlex>
                <RoomAdultBox
                  numAdults={numAdults}
                  numRooms={numRooms}
                  flexDirection={['row', null, null, null, 'column']}
                  px={[3, null, 2, 3, 2]}
                />
              </StyledFlex>
              <StyledFlex flexDirection='column' justifyContent='space-between'>
                <Flex flexDirection={['row', null, 'column']} alignItems='end' justifyContent='center'>
                  <Flex>
                    <Text my='auto' bold fontSize={0} color='secondary' mr='2px' pt={1}>
                      {hotelPriceDetails.currencySymbol || '$'}
                    </Text>
                    <Text color='secondary' bold fontSize={4} lineHeight={1} pt={1}>
                      {hotelPriceDetails.price}
                    </Text>
                    <Text fontSize={0} color='secondary'>
                      *
                    </Text>
                  </Flex>
                  <Text color='secondary' textStyle='disclaimer'>
                    total
                  </Text>
                </Flex>
                <BookNowButton
                  linkUrl={linkUrl}
                  ariaLabel={ariaLabel}
                  onClick={onClick}
                  color='secondary'
                  buttonText={buttonText || 'Book Now'}
                />
              </StyledFlex>
            </StyledFlex>
          ) : (
            <StyledFlex
              flexDirection={
                withSibling ? ['column', null, null, null, null, 'row'] : ['column', null, 'row']
              }
            >
              <StyledFlex>
                <DateBox title='CHECK-IN' date={checkInDate} isSmallerView={isSmallerView} />
                <DateBox title='CHECK-OUT' date={checkOutDate} isSmallerView={isSmallerView} />
              </StyledFlex>
              <StyledFlex
                flexDirection={withSibling ? 'column' : ['column', null, null, null, null, 'row']}
                justifyContent={['space-between', null, null, null, 'center']}
              >
                <RoomAdultBox
                  numAdults={numAdults}
                  numRooms={numRooms}
                  flexDirection='column'
                  px={[3, null, 2, 3, 4, 3]}
                />
                <BookNowButton
                  linkUrl={linkUrl}
                  ariaLabel={ariaLabel}
                  color='primary'
                  onClick={onClick}
                  buttonText={buttonText || 'Continue Booking'}
                />
              </StyledFlex>
            </StyledFlex>
          )}
        </StyledFlex>
      </StyledCard>
    </Relative>
  )
}

export default TripActivity
